import { Card, CardContent, Typography, Box, Chip, Skeleton, Alert, AlertTitle } from "@mui/material";
import { useGetList, useTranslate, usePermissions } from "react-admin";
import { useEffect, useState } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";

import SecurityIcon from "@mui/icons-material/Security";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RouteIcon from "@mui/icons-material/Route";
import CancelIcon from "@mui/icons-material/Cancel";
import { CircularProgress, IconButton, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountDeletionRequestsCard from "./components/AccountDeletionRequestsCard";

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

// Helper for authenticated API calls to main API
const fetchFromApi = async (endpoint: string) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/ld+json',
        },
    });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
};

interface StatCardProps {
    title: string;
    value: number | string;
    icon: React.ReactElement;
    color: string;
    onClick?: () => void;
    loading?: boolean;
}

interface License {
    id: number;
    expiresAt: string | null;
    company: string | number;
    product: string | number;
    active: boolean;
}

interface CompanyLicenseSummary {
    id: number;
    isDemoTenant: boolean;
    licenseStatus: 'none' | 'active' | 'expired';
    licenseExpiresAt: string | null;
}

const StatCard = ({ title, value, icon, color, onClick, loading }: StatCardProps) => (
    <Card sx={{ height: "100%", cursor: onClick ? "pointer" : "default", transition: "all 0.3s ease", "&:hover": onClick ? { transform: "translateY(-4px)", boxShadow: 4 } : {} }} onClick={onClick}>
        <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">{title}</Typography>
                    {loading ? (
                        <Skeleton variant="text" width={60} height={40} />
                    ) : (
                        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: "#1f3240" }}>{value}</Typography>
                    )}
                </Box>
                <Box sx={{ backgroundColor: color, borderRadius: "12px", p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</Box>
            </Box>
        </CardContent>
    </Card>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { permissions } = usePermissions();

    // Rolle-basiertes Gating. roles + products sind Operator-only, der Rest
    // der Admin-Stats (companies, users, invitations, licenses) ist
    // für COMPANY_ADMIN aufwärts zugänglich. Für ROLE_USER/ROLE_INSTRUCTOR
    // würden die Fetches mit 403 antworten — Kacheln und das ganze Dashboard
    // hingen endlos im Loading-Spinner, und jede Admin-Route feuerte dieselben
    // 403er auf einmal. Deshalb gaten wir alle potentiell 403-behafteten
    // Fetches hier zentral.
    const isOperator = Array.isArray(permissions)
        && (permissions.includes('ROLE_OPERATOR') || permissions.includes('ROLE_PLATFORM_ADMIN'));
    const isCompanyAdminOrHigher = Array.isArray(permissions)
        && (permissions.includes('ROLE_OPERATOR')
            || permissions.includes('ROLE_PLATFORM_ADMIN')
            || permissions.includes('ROLE_COMPANY_ADMIN')
            || permissions.includes('ROLE_ADMIN'));

    // Base stats from SSO
    const { total: companiesTotal, isLoading: companiesLoading, data: companiesData } = useGetList<CompanyLicenseSummary>(
        "companies",
        { pagination: { page: 1, perPage: 100 } },
        { enabled: isCompanyAdminOrHigher }
    );
    const { total: usersTotal, isLoading: usersLoading } = useGetList(
        "users",
        { pagination: { page: 1, perPage: 1 } },
        { enabled: isCompanyAdminOrHigher }
    );
    const { total: rolesTotal, isLoading: rolesLoading } = useGetList(
        "roles",
        { pagination: { page: 1, perPage: 1 } },
        { enabled: isOperator }
    );
    const { total: productsTotal, isLoading: productsLoading } = useGetList(
        "products",
        { pagination: { page: 1, perPage: 1 } },
        { enabled: isOperator }
    );
    const { total: licensesTotal, isLoading: licensesLoading, data: licensesData } = useGetList<License>(
        "licenses",
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'expiresAt', order: 'ASC' }
        },
        { enabled: isCompanyAdminOrHigher }
    );

    const [pathRequests, setPathRequests] = useState<any[]>([]);
    const [pathRequestsLoading, setPathRequestsLoading] = useState(true);

    useEffect(() => {
        // Load pending learning path enrollment requests
        const loadPathRequests = async () => {
            try {
                const data = await fetchFromApi('/learning_path_enrollments?status=pending_approval&itemsPerPage=50');
                const enrollments = data['hydra:member'] || data.member || [];

                // Load user details and path details
                const ssoUrl = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';
                const token = localStorage.getItem('token');
                const userIds = Array.from(new Set(enrollments.map((e: any) => e.userId)));
                let usersMap: Record<number, any> = {};
                if (userIds.length > 0) {
                    try {
                        const usersRes = await fetch(`${ssoUrl}/api/users?id[]=${userIds.join('&id[]=')}`, {
                            headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' },
                        });
                        if (usersRes.ok) {
                            const usersData = await usersRes.json();
                            (usersData['hydra:member'] || usersData.member || []).forEach((u: any) => { usersMap[u.id] = u; });
                        }
                    } catch {}
                }

                // Extract path IDs and load path titles
                const pathIds = Array.from(new Set(enrollments.map((e: any) => {
                    const lp = e.learningPath;
                    if (typeof lp === 'object' && lp?.id) return lp.id;
                    if (typeof lp === 'string') { const m = lp.match(/(\d+)/); return m ? parseInt(m[1]) : null; }
                    return typeof lp === 'number' ? lp : null;
                }).filter(Boolean)));
                let pathsMap: Record<number, string> = {};
                for (const pid of pathIds) {
                    try {
                        const pathRes = await fetchFromApi(`/learning_paths/${pid}`);
                        pathsMap[pid as number] = pathRes.title || `Lernpfad #${pid}`;
                    } catch {}
                }

                setPathRequests(enrollments.map((e: any) => {
                    const user = usersMap[e.userId];
                    const pathId = typeof e.learningPath === 'object' ? e.learningPath?.id : (typeof e.learningPath === 'string' ? parseInt(e.learningPath.replace(/\D/g, '')) : e.learningPath);
                    return {
                        ...e,
                        userName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : `User ${e.userId}`,
                        userEmail: user?.email || '',
                        pathTitle: pathsMap[pathId] || `Lernpfad #${pathId}`,
                        pathId,
                    };
                }));
            } catch (error) {
                console.error('Failed to load path requests:', error);
            } finally {
                setPathRequestsLoading(false);
            }
        };

        if (!isCompanyAdminOrHigher) {
            setPathRequestsLoading(false);
            return;
        }

        loadPathRequests();
    }, [isCompanyAdminOrHigher]);

    const productionCompanies = companiesData?.filter(company => !company.isDemoTenant) || [];
    const expiredLicenses = productionCompanies.filter(company => company.licenseStatus !== 'active');
    const expiringLicenses = productionCompanies.filter(company => {
        if (company.licenseStatus !== 'active' || !company.licenseExpiresAt) return false;
        return new Date(company.licenseExpiresAt).getTime() <= Date.now() + 30 * 24 * 60 * 60 * 1000;
    });

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#1f3240", mb: 1 }}>{translate('admin.dashboard.welcome')}</Typography>
            </Box>

            {expiredLicenses.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <AlertTitle>{translate('admin.dashboard.expiredLicenses.title', { smart_count: expiredLicenses.length })}</AlertTitle>
                    {translate('admin.dashboard.expiredLicenses.message', { smart_count: expiredLicenses.length })}
                </Alert>
            )}

            {/* Expiring Licenses Warning */}
            {expiringLicenses.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningAmberIcon />}>
                    <AlertTitle>{translate('admin.dashboard.expiringLicenses.title', { smart_count: expiringLicenses.length })}</AlertTitle>
                    {translate('admin.dashboard.expiringLicenses.message', { smart_count: expiringLicenses.length })}
                </Alert>
            )}

            {/* Main Stats Grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2, mb: 4 }}>
                <StatCard title={translate('admin.dashboard.stats.companies')} value={companiesTotal || 0} icon={<BusinessIcon sx={{ fontSize: 28, color: "white" }} />} color="#1f3240" onClick={() => navigate("/companies")} loading={companiesLoading} />
                <StatCard title={translate('admin.dashboard.stats.users')} value={usersTotal || 0} icon={<PeopleIcon sx={{ fontSize: 28, color: "white" }} />} color="#1cab7e" onClick={() => navigate("/users")} loading={usersLoading} />
                <StatCard title={translate('admin.dashboard.stats.licenses')} value={licensesTotal || 0} icon={<CardMembershipIcon sx={{ fontSize: 28, color: "white" }} />} color="#c0a15e" onClick={() => navigate("/licenses")} loading={licensesLoading} />
            </Box>

            {/* Secondary Stats */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2, mb: 4 }}>
                {isOperator && (
                    <>
                        <StatCard title={translate('admin.dashboard.stats.roles')} value={rolesTotal || 0} icon={<SecurityIcon sx={{ fontSize: 28, color: "white" }} />} color="#ffda65" onClick={() => navigate("/roles")} loading={rolesLoading} />
                        <StatCard title={translate('admin.dashboard.stats.products')} value={productsTotal || 0} icon={<ShoppingCartIcon sx={{ fontSize: 28, color: "white" }} />} color="#ff6d73" onClick={() => navigate("/products")} loading={productsLoading} />
                    </>
                )}
            </Box>

            {/* Dashboard Sections */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f3240", mb: 2 }}>{translate('admin.dashboard.quickAccess')}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, mb: 4 }}>

                {/* System Overview */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <SecurityIcon sx={{ color: "#1cab7e", mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{translate('admin.dashboard.systemOverview.title')}</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">{translate('admin.dashboard.systemOverview.subtitle')}</Typography>
                        <Box sx={{ mt: 2 }}>
                            {isOperator && (
                                <>
                                    <Typography variant="body2" sx={{ py: 1, borderBottom: "1px solid #e1e2e4", display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{translate('admin.dashboard.systemOverview.definedRoles')}</span>
                                        <strong>{rolesTotal || 0}</strong>
                                    </Typography>
                                    <Typography variant="body2" sx={{ py: 1, borderBottom: "1px solid #e1e2e4", display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{translate('admin.dashboard.systemOverview.availableProducts')}</span>
                                        <strong>{productsTotal || 0}</strong>
                                    </Typography>
                                </>
                            )}
                            <Typography variant="body2" sx={{ py: 1, display: 'flex', justifyContent: 'space-between' }}>
                                <span>{translate('admin.dashboard.systemOverview.activeLicenses')}</span>
                                <strong>{licensesData?.filter(l => l.active).length || 0}</strong>
                            </Typography>
                        </Box>
                        {expiredLicenses.length > 0 && (
                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fdecea', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#b71c1c' }}>
                                    <WarningAmberIcon sx={{ fontSize: 18, mr: 1 }} />
                                    {translate('admin.dashboard.systemOverview.expiredLicensesWarning', { smart_count: expiredLicenses.length })}
                                </Typography>
                            </Box>
                        )}
                        {expiringLicenses.length > 0 && (
                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#e65100' }}>
                                    <WarningAmberIcon sx={{ fontSize: 18, mr: 1 }} />
                                    {translate('admin.dashboard.systemOverview.expiringLicensesWarning', { smart_count: expiringLicenses.length })}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {isCompanyAdminOrHigher && <AccountDeletionRequestsCard showCompany={isOperator} />}

            {/* Pending Learning Path Enrollment Requests */}
            {pathRequests.length > 0 && (
            <Box sx={{ mb: 4 }}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RouteIcon color="warning" />
                                Offene Lernpfad-Anfragen
                                <Chip label={pathRequests.length} size="small" color="warning" />
                            </Typography>
                        </Box>
                        {pathRequestsLoading ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                        ) : (
                            pathRequests.map((req: any, idx: number) => (
                                <Box key={req.id}>
                                    {idx > 0 && <Divider sx={{ my: 1 }} />}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={500} noWrap>
                                                {req.userName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {req.pathTitle}
                                                {req.userEmail ? ` · ${req.userEmail}` : ''}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                title="Genehmigen"
                                                onClick={async () => {
                                                    try {
                                                        await fetchFromApi(`/learning-path-enrollments/${req.id}/approve`).catch(() =>
                                                            fetch(`${API_URL}/learning-path-enrollments/${req.id}/approve`, {
                                                                method: 'POST',
                                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                                                            })
                                                        );
                                                        setPathRequests(prev => prev.filter(r => r.id !== req.id));
                                                    } catch {}
                                                }}
                                            >
                                                <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                title="Ablehnen"
                                                onClick={async () => {
                                                    try {
                                                        await fetch(`${API_URL}/learning-path-enrollments/${req.id}/reject`, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                                                        });
                                                        setPathRequests(prev => prev.filter(r => r.id !== req.id));
                                                    } catch {}
                                                }}
                                            >
                                                <CancelIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </CardContent>
                </Card>
            </Box>
            )}
        </Box>
    );
};

export default Dashboard;
